<template>
  <div
    class="home-page"
    :class="{'home-page--corteco': isCortecoUser}"
  >
    <client-only>
      <IntroJs v-if="$auth.loggedIn" />
    </client-only>
    <HomeHero
      :content="heroSection"
    />
    <div class="home-page__content-container">
      <h2 v-if="topCategories && topCategories.length > 0">
        {{ i18n.tv('ProductCategories', 'home', 'Product categories') }}
      </h2>
      <div
        v-if="topCategories && topCategories.length > 0"
        class="home-page__content-container__top-category-list"
      >
        <CategoryTileNew
          v-for="(category, index) in topCategories"
          :key="category.id + '_' + index"
          :category="category"
          :type="'medium'"
        />
      </div>
      <div
        v-if="topCategories && topCategories.length > 0"
        class="home-page__content-container__show-more"
      >
        <nuxt-link
          :to="localePath(CATEGORIES.RootCategoriesUrl)"
          class="k-button k-button-md k-button-solid k-button-solid-primary"
        >
          <span>
            {{ i18n.tv('showAllProducts', 'home') }}
          </span>
        </nuxt-link>
      </div>
    </div>

    <div
      v-if="isCortecoUser"
      class="home-page__corteco-section"
    >
      <div class="home-page__corteco-section__content">
        <div class="home-page__corteco-section__content__text">
          <Markdown :text="i18n.tv('footerEasyDescription', 'home')" />
        </div>
        <div class="home-page__corteco-section__content__logo">
          <img loading="lazy"
            :src="CortecoLogo"
            alt="Corteco"
          >
        </div>
        <div class="home-page__corteco-section__content__logo">
          <img loading="lazy"
            :src="TranstecLogo"
            alt="Transtec"
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import CategoryTileNew from '~/components/category/CategoryTileNew'
import HomeHero from '~/components/home/HomeHero'
import {CATEGORIES} from '~/assets/js/constants'
import IntroJs from '~/components/intro/IntroJs'
import Markdown from '~/components/Markdown'
import useI18nExtended from '~/composables/usei18nExtended'
import usePim from '~/composables/pim_json_loader'
import {useHomeHero} from '~/composables/useHomeHero'
import CortecoLogo from '~/assets/images/Corteco_Logo_RGB.png'
import TranstecLogo from '~/assets/images/Transtec_logo_black_RGB-01.png'

const nuxtApp = useNuxtApp()
const i18n = useI18nExtended()
const region = nuxtApp.$globalization.getRegion()

const startPageJson = await usePim(`v1/${region.toLocaleLowerCase()}/${i18n.locale.value}/startpage.json`)
const topCategories = startPageJson

const {isCortecoUser, heroSection} = useHomeHero()
</script>

<style lang="scss" scoped>
.home-page {
  background-color: $color__content--background__third;
  margin-top: -.5rem;

  &--corteco {
    padding-bottom: 0;
  }

  &__content-container {
    display: flex;
    flex-flow: row wrap;
    padding: 1.5rem $default-margin-padding-2x $default-margin-padding-2x;
    background-color: $color__content--background__third;

    h2 {
      margin-bottom: 1.5rem;
      margin-top: 0;
    }

    .home-page--corteco & {
      padding-bottom: 0;
    }

    @include breakpoint-down($md) {
      padding: $default-margin-padding-width $default-margin-padding-width 0;
    }

    $gap: 1.25rem;

    &__top-category-list {
      display: flex;
      flex-wrap: wrap;
      flex: 1 1 100%;
      order: 1;
      margin: -#{$gap} 0 0 -#{$gap};
      width: calc(100% + #{$gap});

      > .category-tile {
        display: inline-block;
        margin: #{$gap} 0 0 #{$gap};
      }
    }

    &__show-more {
      flex-basis: 100%;
      margin-top: 1.5rem;
      margin-bottom: 2.0625rem;
      display: flex;
      justify-content: flex-end;
      order: 10;
      font-weight: $base-text-font-weight-light;

      .k-button-solid-primary {
        background-color: $color__primary;
        color: $color__white;
      }

      @include breakpoint-down($xl) {
        flex: 0 0 auto;
        order: 3;
        justify-content: flex-end;
        margin-top: 1.875rem;
        margin-bottom: 1.25rem;
      }

      @include breakpoint-down($between_md_lg) {
        justify-content: flex-start;
      }

      @include breakpoint-down($md) {
        margin-bottom: 0;
      }

      > a {
        display: inline-flex;
        justify-content: center;
        align-items: center;
      }
    }

    @include breakpoint-down($xl) {
      flex-direction: column;

      &__top-category-list {
        flex: 0 0 auto;
      }
    }
  }

  &__corteco-section {
    background-color: $color__white;
    padding: 2rem 2rem 12.5rem;

    @include breakpoint-down($md) {
      padding: 2rem 2rem 2.5rem;
    }

    &__content {
      display: flex;
      justify-content: space-between;
      gap: 1.25rem;
      max-width: 75rem;

      @include breakpoint-down($md) {
        flex-wrap: wrap;
        justify-content: flex-start;
        gap: 1.25rem;
      }

      &__text {
        flex: 1;
        min-width: 0;

        :deep(h2) {
          font-size: 1.5rem;
          margin: 0;
          color: $color__primary;
        }

        :deep(p),
        :deep(li) {
          font-weight: $base-text-font-weight-light;
          line-height: 1.138rem;
        }

        :deep(li)::marker {
          font-size: 0.75rem;
        }

        :deep(p) {
          margin: 0.5rem 0 0;
        }

        :deep(ul) {
          margin: 0.5rem 0 0.5rem -1rem;
        }

        @include breakpoint-down($md) {
          flex: 1 1 100%;

          :deep(h2) {
            font-size: $heading-h5-font-size;
          }
        }
      }

      &__logo {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 0;

        @include breakpoint-down($md) {
          flex: 0 1 calc(50% - 0.75rem);
          max-width: 9.375rem;
        }

        img {
          width: 100%;
          height: auto;
          max-width: 100%;
          object-fit: contain;
        }
      }
    }
  }
}
</style>
